import React from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AlertProvider } from './contexts/AlertContext';
import { LoginPage } from './pages/LoginPage';
import { SsoPage } from './pages/SsoPage';
import { Shell } from './components/Shell';
import { DashboardPage } from './pages/DashboardPage';
import { VehicleListPage } from './pages/VehicleListPage';
import { VehicleDetailPage } from './pages/VehicleDetailPage';
import { CompanyListPage } from './pages/CompanyListPage';
import { CompanyDetailPage } from './pages/CompanyDetailPage';
import { DealDetailPage } from './pages/DealDetailPage';
import { LedgerPage } from './pages/LedgerPage';
import { VicsPage } from './pages/VicsPage';
import { LpReportPage } from './pages/LpReportPage';
import { ClosingPage } from './pages/ClosingPage';
import { InternalReportPage } from './pages/InternalReportPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { PeriodicReportPage } from './pages/PeriodicReportPage';
import { RawDataPage } from './pages/RawDataPage';
import { RiskReviewPage } from './pages/RiskReviewPage';
import { AiPage } from './pages/AiPage';

/* 워크플로우 순서대로 채웠다 — 자금원 → 투자처 → 투자 건 → 원장 → 지표.
 * 자금원이 먼저인 이유는 통장이 없으면 투자를 적을 곳이 없기 때문이다. */
/** 로그인해야 보이는 곳 — 안 했으면 로그인 화면으로, 다녀오면 원래 자리로 */
const Protected: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const { pathname, search } = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> 확인 중...</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: pathname + search }} />;
  return <>{children}</>;
};

function App() {
  return (
    <AuthProvider>
    <AlertProvider>
    <BrowserRouter>
      <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* admin 바로 이동(handbook #16) */}
      <Route path="/sso" element={<SsoPage />} />
      <Route path="*" element={<Protected>
      <Shell>
        <Routes>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/vehicles" element={<VehicleListPage />} />
          <Route path="/vehicles/:id" element={<VehicleDetailPage />} />
          <Route path="/companies" element={<CompanyListPage />} />
          <Route path="/companies/:id" element={<CompanyDetailPage />} />
          {/* 「투자 건」 메뉴는 투자처 안으로 합쳤다(2026-11-02) — 상세 주소(/deals/:id)는 다른 화면 링크용으로 남긴다 */}
          <Route path="/deals" element={<Navigate to="/companies" replace />} />
          <Route path="/deals/:id" element={<DealDetailPage />} />
          <Route path="/ledger" element={<LedgerPage />} />
          <Route path="/reports/periodic" element={<PeriodicReportPage />} />
          <Route path="/reports/risk" element={<RiskReviewPage />} />
          <Route path="/reports/vics" element={<VicsPage />} />
          <Route path="/reports/lp" element={<LpReportPage />} />
          <Route path="/reports/closing" element={<ClosingPage />} />
          <Route path="/reports/internal" element={<InternalReportPage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/data" element={<RawDataPage />} />
          <Route path="/ai" element={<AiPage />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Shell>
      </Protected>} />
      </Routes>
    </BrowserRouter>
    </AlertProvider>
    </AuthProvider>
  );
}

export default App;
